/**
 * Online Food Ordering Cart Store
 * Zustand store for shopping cart state management
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { CartItem, CartModifier, OrderType } from '@/types';

interface CartState {
  // State - ONLY cart data, no calculated fees or addresses
  restaurantId: string | null;
  items: CartItem[];
  orderType: OrderType;
  promoCode: string | null;
  promoDiscount: number;

  // Actions
  addItem: (item: Omit<CartItem, 'id'>) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateQuantityByMenuItemId: (menuItemId: string, quantity: number) => void;
  updateItem: (itemId: string, updates: { quantity?: number; notes?: string }) => void;
  removeItem: (itemId: string) => void;
  clearCart: () => void;
  setOrderType: (type: OrderType) => void;
  setPromoCode: (code: string | null, discount: number) => void;
  setRestaurantId: (restaurantId: string) => void;
}

// Generate unique ID for cart items
const generateItemId = () => `cart_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

// Check if two items have the same modifiers
const areModifiersEqual = (a: CartModifier[], b: CartModifier[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x.option_id.localeCompare(y.option_id));
  const sortedB = [...b].sort((x, y) => x.option_id.localeCompare(y.option_id));
  return sortedA.every((mod, i) => mod.option_id === sortedB[i]?.option_id);
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      // Initial state
      restaurantId: null,
      items: [],
      orderType: 'delivery' as OrderType,
      promoCode: null,
      promoDiscount: 0,

      // Actions
      addItem: (newItem) => {
        set((state) => {
          // Check if same item with same modifiers exists
          const existingIndex = state.items.findIndex(
            (item) =>
              item.menu_item_id === newItem.menu_item_id &&
              areModifiersEqual(item.modifiers, newItem.modifiers) &&
              item.notes === newItem.notes
          );

          if (existingIndex !== -1) {
            // Update quantity of existing item
            const updatedItems = [...state.items];
            const existingItem = updatedItems[existingIndex];
            if (existingItem) {
              existingItem.quantity += newItem.quantity;
            }
            return { items: updatedItems };
          }

          // Add as new item
          const itemWithId: CartItem = {
            ...newItem,
            id: generateItemId(),
          };
          return { items: [...state.items, itemWithId] };
        });
      },

      updateQuantity: (itemId, quantity) => {
        set((state) => {
          if (quantity <= 0) {
            return { items: state.items.filter((item) => item.id !== itemId) };
          }
          return {
            items: state.items.map((item) => (item.id === itemId ? { ...item, quantity } : item)),
          };
        });
      },

      // Used by the chat widget to avoid stale-closure ID lookups
      updateQuantityByMenuItemId: (menuItemId, quantity) => {
        set((state) => {
          const target = state.items.find((item) => item.menu_item_id === menuItemId);
          if (!target) return state;
          if (quantity <= 0) {
            return { items: state.items.filter((item) => item.menu_item_id !== menuItemId) };
          }
          return {
            items: state.items.map((item) =>
              item.menu_item_id === menuItemId ? { ...item, quantity } : item
            ),
          };
        });
      },

      updateItem: (itemId, updates) => {
        set((state) => {
          if (updates.quantity !== undefined && updates.quantity <= 0) {
            return { items: state.items.filter((item) => item.id !== itemId) };
          }
          return {
            items: state.items.map((item) => (item.id === itemId ? { ...item, ...updates } : item)),
          };
        });
      },

      removeItem: (itemId) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== itemId),
        }));
      },

      clearCart: () => {
        set({
          items: [],
          promoCode: null,
          promoDiscount: 0,
        });
      },

      setOrderType: (type) => {
        set({ orderType: type });
      },

      setPromoCode: (code, discount) => {
        set({ promoCode: code, promoDiscount: discount });
      },

      setRestaurantId: (restaurantId) => {
        const currentRestaurantId = get().restaurantId;
        // Clear cart if switching restaurants
        if (currentRestaurantId && currentRestaurantId !== restaurantId) {
          set({
            restaurantId,
            items: [],
            promoCode: null,
            promoDiscount: 0,
          });
        } else {
          set({ restaurantId });
        }
      },
    }),
    {
      name: 'food-ordering-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        restaurantId: state.restaurantId,
        items: state.items,
        orderType: state.orderType,
        promoCode: state.promoCode,
        promoDiscount: state.promoDiscount,
      }),
    }
  )
);

// Selector hooks for computed values
export const useCartItemCount = () =>
  useCartStore((state) => state.items.reduce((sum, item) => sum + item.quantity, 0));

export const useCartSubtotal = () =>
  useCartStore((state) =>
    state.items.reduce((sum, item) => {
      const modifiersTotal = item.modifiers.reduce((m, mod) => m + mod.price_adjustment, 0);
      return sum + (item.unit_price + modifiersTotal) * item.quantity;
    }, 0)
  );

// Get quantity of a specific menu item in cart (by menu_item_id)
export const useCartItemQuantityByMenuItemId = (menuItemId: string) =>
  useCartStore((state) =>
    state.items
      .filter((item) => item.menu_item_id === menuItemId)
      .reduce((sum, item) => sum + item.quantity, 0)
  );
